import React, { useState, useEffect } from "react";
import * as Mui from "@mui/material";
import { GlobalDialogBox } from "components/GlobalDialogBox";
import { themes } from "services/constants";
import { Close } from "@mui/icons-material";
import { useFetch } from "services/hooks/useFetch";
import { useSelector } from "react-redux";
import sessionHandling from "services/utils/notificationUtils";
import { CustomAutoComplete } from "components/CustomComponents/CustomAutoComplete";
import { showToast } from "services/utils/Status";

export const AssignWorkUnit = ({
  userShiftsDet,
  curShiftDetail,
  shiftName,
  setOpenEditWorkUnitDialogBox,
  editWorkUnitDialogBox,
  weeklyScreen,
  setWeeklyScreen,
  setFetchWeekly,
}) => {
  const { departmentId } = useSelector((state) => state.userReducer);
  const { domain, token } = useSelector((state) => state.tokenReducer);
  const [newlyAddedWorkUnits, setNewlyAddedWorkUnits] = useState([]);
  const { result: workUnitDetails } = useFetch(
    `${domain}work_units/?department=${departmentId}`
  );
  const [workUnitDetailsValue, setWorkUnitDetails] = useState([]);
  const [overAllData, setOverAllData] = useState([]);
  const [updateLabel, setUpdateLabel] = useState(true);
  const [rowErrors, setRowErrors] = useState({});

  const addedWorkUnitNames =
    curShiftDetail?.additional_units?.details?.map(
      (unit) => unit?.work_unit_name
    ) || [];

  const filteredWorkUnitDetails = workUnitDetailsValue.filter(
    (item) => !addedWorkUnitNames.includes(item.name)
  );

  useEffect(() => {
    if (updateLabel && workUnitDetails.length !== 0) {
      let updatedWorkUnitDetails = workUnitDetails.map((data) => {
        return {
          ...data,
          label: data.work_unit_name,
        };
      });
      setWorkUnitDetails(updatedWorkUnitDetails);
      setOverAllData(updatedWorkUnitDetails);
      setUpdateLabel(false);
    }
  }, [updateLabel, workUnitDetails]);

  const addNewRow = () => {
    if (newlyAddedWorkUnits.length > 0) {
      const lastIndex = newlyAddedWorkUnits.length - 1;
      const lastRow = newlyAddedWorkUnits[lastIndex];

      if (!lastRow?.name || !lastRow?.number_of_units) {
        setRowErrors((prev) => ({
          ...prev,
          [lastRow.id]: {
            name: !lastRow?.name ? "Work unit name is required" : "",
            number_of_units: !lastRow?.number_of_units
              ? "Number of units is required"
              : "",
          },
        }));
        return;
      }

      setRowErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[lastRow.id];
        return newErrors;
      });
    }

    let row = {
      id: newlyAddedWorkUnits.length + 1,
      name: "",
      work_unit: 0,
      number_of_units: 0,
    };
    setNewlyAddedWorkUnits([...newlyAddedWorkUnits, row]);
  };

  const handleWorkUnit = (details) => (event, data) => {
    if (data) {
      let updatedData = newlyAddedWorkUnits.map((element) => {
        if (element.id === details.id) {
          return {
            ...element,
            name: data.label,
            label: data.label,
            work_unit: data.id,
          };
        } else {
          return element;
        }
      });
      if (rowErrors?.[details.id]?.name) {
        setRowErrors((prev) => ({
          ...prev,
          [details.id]: {
            ...prev[details.id],
            name: "",
          },
        }));
      }
      setNewlyAddedWorkUnits(updatedData);
      removeSelectedData(updatedData);
    }
  };

  function getCSRFToken() {
    return document.cookie
      .split("; ")
      .find((row) => row.startsWith("csrftoken="))
      ?.split("=")[1];
  }

  const addAdtionalWorkUnits = async (currentModifyDetails) => {
    if (newlyAddedWorkUnits?.length > 0) {
      let hasError = false;
      const newErrors = {};
      newlyAddedWorkUnits.forEach((row) => {
        const rowError = {};
        if (!row.name) {
          rowError.name = "Work unit name is required";
          hasError = true;
        }
        if (!row.number_of_units || row.number_of_units === 0) {
          rowError.number_of_units = "Number of units is required";
          hasError = true;
        }
        if (Object.keys(rowError).length > 0) {
          newErrors[row.id] = rowError;
        }
      });

      if (hasError) {
        setRowErrors(newErrors);
        return;
      }
      setRowErrors({});
      let updateData = newlyAddedWorkUnits.filter((data) => {
        return (
          data.name !== "" &&
          data.number_of_units !== 0 &&
          data.number_of_units !== ""
        );
      });
      let payload = {
        user: userShiftsDet?.user_id,
        date: currentModifyDetails?.date?.split("-").reverse().join("-"),
        additional_units: updateData.map(({ name, label, ...rest }) => rest),
      };
      try {
        await fetch(`${domain}csrf/`, { credentials: "include" });
        const response = await fetch(`${domain}unit/additional-units/`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
            "X-CSRFToken": getCSRFToken(),
          },
          body: JSON.stringify(payload),
        });

        const data = await response.json();

        if (response.ok) {
          showToast("success", data?.responseDetails?.message || "Success");
          setOpenEditWorkUnitDialogBox(false);
          setNewlyAddedWorkUnits([]);
          if (weeklyScreen) {
            setFetchWeekly(true);
            setWeeklyScreen(false);
          }
          setUpdateLabel(true);
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error(response.error);
        }
      } catch (error) {
        showToast("error", error.message);
      }
    } else if (
      newlyAddedWorkUnits?.length === 0 &&
      currentModifyDetails?.additional_units?.details?.length > 0
    ) {
      showToast(
        "success",
        "Additional units assigned or updated successfully."
      );
      setOpenEditWorkUnitDialogBox(false);
    } else {
      showToast("error", "Select a work unit to proceed.");
    }
  };

  const removeRow = (rowDetails) => {
    let updatedData = newlyAddedWorkUnits.filter((details) => {
      return details.id !== rowDetails.id;
    });
    setNewlyAddedWorkUnits(updatedData);
    removeSelectedData(updatedData);
  };

  const removeSelectedData = (updatedData) => {
    let overAllDetails = overAllData;
    updatedData.forEach((data) => {
      overAllDetails = overAllDetails.filter((details) => {
        return details.label !== data.label;
      });
    });
    setWorkUnitDetails(overAllDetails);
  };
  return (
    <>
      <GlobalDialogBox
        handleCloseDialog={() => {
          setOpenEditWorkUnitDialogBox(false);
          setNewlyAddedWorkUnits([]);
          setUpdateLabel(true);
        }}
        open={editWorkUnitDialogBox}
        title="Assign work unit"
        minWidth={650}
      >
        <Mui.Grid container sx={{ paddingBottom: 2 }}>
          <Mui.Grid xs={4}>
            <Mui.Typography sx={{ fontWeight: "bold" }}>Name</Mui.Typography>
            <Mui.Box
              sx={{
                width: 150,
                height: 15,
                border: `2px solid ${themes.sidebarDivider}`,
                borderRadius: "6px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                color: themes.headLine,
                p: 2,
                fontSize: 13,
              }}
            >
              {userShiftsDet.user_name}
            </Mui.Box>
          </Mui.Grid>
          <Mui.Grid xs={5}>
            <Mui.Typography sx={{ fontWeight: "bold" }}>
              Shift Name
            </Mui.Typography>
            <Mui.Box
              sx={{
                width: 200,
                height: 15,
                border: `2px solid ${themes.sidebarDivider}`,
                borderRadius: "6px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                color: themes.headLine,
                p: 2,
                fontSize: 13,
              }}
            >
              {shiftName}
            </Mui.Box>
          </Mui.Grid>
          <Mui.Grid xs={3}>
            <Mui.Typography sx={{ fontWeight: "bold" }}>Date</Mui.Typography>
            <Mui.Box
              sx={{
                width: 120,
                height: 15,
                border: `2px solid ${themes.sidebarDivider}`,
                borderRadius: "6px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                color: themes.headLine,
                p: 2,
                fontSize: 13,
              }}
            >
              {curShiftDetail.date}
            </Mui.Box>
          </Mui.Grid>
        </Mui.Grid>
        {/* Defalut work Unit */}

        <Mui.Typography sx={{ fontWeight: "bold" }}>
          Default Unit
        </Mui.Typography>
        <Mui.Grid container sx={{ paddingTop: 1 }}>
          <Mui.Grid container>
            <Mui.Grid xs={6}>
              <Mui.Typography sx={{ fontWeight: "bold" }}>
                Unit Name
              </Mui.Typography>
            </Mui.Grid>
            <Mui.Grid xs={6}>
              <Mui.Typography sx={{ fontWeight: "bold" }}>
                Unit Number
              </Mui.Typography>
            </Mui.Grid>
          </Mui.Grid>
          {curShiftDetail?.default_units?.details?.map((data) => (
            <Mui.Grid
              container
              sx={{
                borderTop: `2px solid ${themes.sidebarDivider}`,
                borderBottom: `2px solid ${themes.sidebarDivider}`,
              }}
            >
              <Mui.Grid xs={6}>
                <Mui.Typography>{data?.work_unit_name}</Mui.Typography>
              </Mui.Grid>
              <Mui.Grid xs={6}>
                <Mui.Typography>{data?.number_of_units}</Mui.Typography>
              </Mui.Grid>
            </Mui.Grid>
          ))}
        </Mui.Grid>
        <Mui.Typography sx={{ fontWeight: "bold" }}>
          Additional Unit
        </Mui.Typography>
        <Mui.Grid container sx={{ paddingTop: 1 }}>
          <Mui.Grid container>
            <Mui.Grid xs={6}>
              <Mui.Typography sx={{ fontWeight: "bold" }}>
                Unit Name
              </Mui.Typography>
            </Mui.Grid>
            <Mui.Grid xs={6}>
              <Mui.Typography sx={{ fontWeight: "bold" }}>
                Unit Number
              </Mui.Typography>
            </Mui.Grid>
          </Mui.Grid>
          {curShiftDetail?.additional_units?.details?.map((data) => (
            <Mui.Grid
              container
              sx={{
                borderTop: `2px solid ${themes.sidebarDivider}`,
                borderBottom: `2px solid ${themes.sidebarDivider}`,
              }}
            >
              <Mui.Grid xs={6}>
                <Mui.Typography>{data?.work_unit_name}</Mui.Typography>
              </Mui.Grid>
              <Mui.Grid xs={6}>
                <Mui.Typography>{data?.number_of_units}</Mui.Typography>
              </Mui.Grid>
            </Mui.Grid>
          ))}
          {newlyAddedWorkUnits?.map((data) => (
            <Mui.Grid
              container
              sx={{
                borderTop: `2px solid ${themes.sidebarDivider}`,
                borderBottom: `2px solid ${themes.sidebarDivider}`,
              }}
            >
              <Mui.Grid xs={5} sx={{ padding: 1 }}>
                <CustomAutoComplete
                  dataList={filteredWorkUnitDetails}
                  selectedValue={data?.name}
                  updateDataFunction={handleWorkUnit(data)}
                  placeHolder="Select work unit name"
                  fullWith
                  errorMessage={rowErrors?.[data.id]?.name}
                  textBoxError={!!rowErrors?.[data.id]?.name}
                />
              </Mui.Grid>
              <Mui.Grid xs={5} sx={{ padding: 1 }}>
                <Mui.TextField
                  size="small"
                  value={data?.number_of_units}
                  onChange={(event) => {
                    const value = event.target.value;
                    if (/^\d*$/.test(value)) {
                      let updatedData = newlyAddedWorkUnits.map((element) => {
                        if (element.id === data.id) {
                          return {
                            ...element,
                            number_of_units: event.target.value,
                          };
                        } else {
                          return element;
                        }
                      });
                      setNewlyAddedWorkUnits(updatedData);
                    }
                    setRowErrors((prev) => ({
                      ...prev,
                      [data.id]: {
                        ...prev[data.id],
                        number_of_units: "",
                      },
                    }));
                  }}
                  error={!!rowErrors?.[data.id]?.number_of_units}
                  helperText={rowErrors?.[data.id]?.number_of_units}
                  fullWidth
                />
              </Mui.Grid>
              <Mui.Grid xs={2} sx={{ paddingTop: 1 }}>
                <Mui.IconButton
                  onClick={() => {
                    removeRow(data);
                    setRowErrors((prev) => {
                      const updated = { ...prev };
                      delete updated[data.id];
                      return updated;
                    });
                  }}
                >
                  <Close />
                </Mui.IconButton>
              </Mui.Grid>
            </Mui.Grid>
          ))}
          <Mui.Grid
            container
            sx={{
              display: "flex",
              flexDirection: "row",
              alignSelf: "flex-end",
              justifyContent: "flex-end",
              paddingTop: 5,
            }}
          >
            <Mui.Grid xs={9}></Mui.Grid>
            <Mui.Grid xs={3}>
              <Mui.Button
                sx={{
                  background: themes.primaryButton,
                  textTransform: "capitalize",
                  "&:hover": {
                    backgroundColor: themes.primaryButton,
                  },
                  color: themes.headLine,
                  fontWeight: "bold",
                }}
                fullWidth
                onClick={() => {
                  addNewRow();
                }}
              >
                Add
              </Mui.Button>
            </Mui.Grid>
          </Mui.Grid>
        </Mui.Grid>
        <Mui.Grid container sx={{ paddingTop: 2 }}>
          <Mui.Button
            sx={{
              background: themes.primaryButton,
              textTransform: "capitalize",
              "&:hover": {
                backgroundColor: themes.primaryButton,
              },
              color: themes.headLine,
              fontWeight: "bold",
            }}
            fullWidth
            onClick={() => {
              addAdtionalWorkUnits(curShiftDetail);
            }}
          >
            Update
          </Mui.Button>
        </Mui.Grid>
      </GlobalDialogBox>
    </>
  );
};
